// src/MobileShiftForm.js
// 多月表示版: 現在月+1 ヶ月から 1 年先までを閲覧可能。
//   - 各月は「その月の提出締切日 ((月-1).deadlineDay)」を過ぎていればロック (閲覧のみ)。
//   - ロック月は 変更希望 ボタンから shift_change_requests へ通知する。
import React, { useState, useEffect, useRef } from "react";
import { supabase } from "./supabase";
import {
  Calendar,
  X,
  Check,
  Send,
  Loader2,
  Info,
  AlertCircle,
  CheckCircle,
  Lock,
  MessageSquare
} from "lucide-react";
import { useOrganization } from './contexts/OrganizationContext';
import { writeClientAudit } from './utils/auditClient';

const formatDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// 期間(startMonth〜endMonth、毎年繰り返し)が、対象年月(targetYear, targetMonth1to12)を含むか判定し、
// 含む場合は具体的な期間ウィンドウ {startY, startM, endY, endM} を返す（毎年同じ月で年を解決）
const resolvePeriodWindow = (startMonth, endMonth, targetYear, targetMonth) => {
  const sM = Number(startMonth);
  const eM = Number(endMonth);
  const tM = Number(targetMonth);
  if (!sM || !eM || !tM) return null;
  const inRange = sM <= eM
    ? (tM >= sM && tM <= eM)
    : (tM >= sM || tM <= eM); // 年跨ぎ
  if (!inRange) return null;
  if (sM <= eM) {
    return { startY: targetYear, startM: sM, endY: targetYear, endM: eM };
  }
  // 年跨ぎ: targetMonth が startMonth 以降なら期間は targetYear 始まり、そうでなければ targetYear-1 始まり
  if (tM >= sM) {
    return { startY: targetYear, startM: sM, endY: targetYear + 1, endM: eM };
  }
  return { startY: targetYear - 1, startM: sM, endY: targetYear, endM: eM };
};

// 期間ウィンドウの先頭月（YYYY-MM-01）と最終月（YYYY-MM-末日）の文字列を返す
const periodWindowToDateRange = (win) => {
  const start = `${win.startY}-${String(win.startM).padStart(2, '0')}-01`;
  const lastDay = new Date(win.endY, win.endM, 0).getDate();
  const end = `${win.endY}-${String(win.endM).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  return { start, end };
};

// ==========================================
// 月単位ヘルパー + サイクル解決
// ==========================================
// legacy 月次 (= cycle 未登録時): その月の「提出締切日」= (月-1).deadlineDay 23:59:59
const getDeadlineForMonth = (monthDate, deadlineDay) => {
  const y = monthDate.getFullYear();
  const m = monthDate.getMonth();
  return new Date(y, m - 1, deadlineDay, 23, 59, 59);
};
// 初期表示月: ロックされていない最初の月 (= 「次に希望を出せる月」)
const calcInitialDisplayMonth = (deadlineDay) => {
  const today = new Date();
  const deadline = new Date(today.getFullYear(), today.getMonth(), deadlineDay, 23, 59, 59);
  if (today <= deadline) {
    return new Date(today.getFullYear(), today.getMonth() + 1, 1);
  }
  return new Date(today.getFullYear(), today.getMonth() + 2, 1);
};
// 表示する候補月: 過去 6 ヶ月 + 現在月 + 1 年先まで (= 過去の希望も閲覧可能に)
const buildAvailableMonths = () => {
  const today = new Date();
  const months = [];
  for (let i = -6; i <= 12; i++) {
    months.push(new Date(today.getFullYear(), today.getMonth() + i, 1));
  }
  return months;
};
const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
const monthLabel = (d) => `${d.getFullYear()}年${d.getMonth() + 1}月`;
const parseLocalDate = (ymd) => new Date(ymd + 'T00:00:00');

// サイクル解決: その月と期間が重なる cycle を割り当てる (月跨ぎ cycle は両方の月から見える)
const findCycleForMonth = (monthDate, cycles) => {
  const y = monthDate.getFullYear();
  const m = monthDate.getMonth();
  const startOfMonth = new Date(y, m, 1);
  const endOfMonth = new Date(y, m + 1, 0, 23, 59, 59, 999);
  return (cycles || []).find((c) => {
    const start = parseLocalDate(c.target_period_start);
    const end = parseLocalDate(c.target_period_end);
    // 期間と月が交差 (start <= 月末 かつ end >= 月初)
    return start <= endOfMonth && end >= startOfMonth;
  }) || null;
};
// 月タブに対応する「期間」と「締切」を返す (cycle 優先、無ければ legacy)
const resolvePeriodForMonth = (monthDate, cycles, deadlineDay) => {
  const cycle = findCycleForMonth(monthDate, cycles);
  if (cycle) {
    return {
      kind: 'cycle',
      cycle,
      periodStart: parseLocalDate(cycle.target_period_start),
      periodEnd: parseLocalDate(cycle.target_period_end),
      deadline: new Date(cycle.submission_deadline),
      label: cycle.name || null,
    };
  }
  // cycle 未定義: cycles 登録済み組織では「対象外」、未登録組織では legacy
  if ((cycles || []).length > 0) return null;
  if (deadlineDay == null) return null;
  const y = monthDate.getFullYear();
  const m = monthDate.getMonth();
  return {
    kind: 'legacy',
    cycle: null,
    periodStart: new Date(y, m, 1),
    periodEnd: new Date(y, m + 1, 0),
    deadline: getDeadlineForMonth(monthDate, deadlineDay),
    label: null,
  };
};
const isLocked = (resolvedPeriod) =>
  resolvedPeriod ? new Date() > resolvedPeriod.deadline : false;
// その月が過去月か (末日 < 今日)
const isPastMonth = (monthDate) => {
  if (!monthDate) return false;
  const endOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0, 23, 59, 59, 999);
  return endOfMonth < new Date();
};
// year_month は常にタブ (= カレンダー月) ベース。
//   cycle が月を跨ぐ場合でも、各タブは自分の月分だけを担当する。
//   希望回数は「その月に対しての計算」なので、こうしないと月跨ぎ cycle で
//   両タブが同じデータを読み込み count が合算されてしまう。
const yearMonthForMonth = (monthDate) => {
  if (!monthDate) return null;
  return `${monthDate.getFullYear()}-${String(monthDate.getMonth() + 1).padStart(2, '0')}`;
};
const formatPeriodShort = (p) => {
  if (!p) return '';
  const s = p.periodStart; const e = p.periodEnd;
  const fmt = (d) => `${d.getMonth() + 1}/${d.getDate()}`;
  return `${fmt(s)}〜${fmt(e)}`;
};
const formatDeadlineText = (d) => {
  if (!d) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

function MobileShiftForm() {
  const { currentOrganization, currentUser, isLoading } = useOrganization();

  // deadlineDay が確定するまで targetMonth は null
  // targetMonth = 現在ユーザーが表示している月 (ユーザーが切り替えられる)
  const [deadlineDay, setDeadlineDay] = useState(null);
  const [targetMonth, setTargetMonth] = useState(null);
  const [availableMonths, setAvailableMonths] = useState([]);
  // サイクル (shift_submission_cycles): 定義があれば cycle 優先、無ければ legacy
  const [cycles, setCycles] = useState([]);
  // 月タブ切り替え用の ref (選択中タブに自動スクロール)
  const tabsContainerRef = useRef(null);
  const activeTabRef = useRef(null);
  // 変更希望モーダル
  const [changeRequestOpen, setChangeRequestOpen] = useState(false);
  const [changeRequestDate, setChangeRequestDate] = useState(null);
  const [changeRequestNote, setChangeRequestNote] = useState("");
  const [changeRequestDesired, setChangeRequestDesired] = useState("");
  const [changeRequestSubmitting, setChangeRequestSubmitting] = useState(false);
  const [changeRequestStatus, setChangeRequestStatus] = useState("");

  const [calendarData, setCalendarData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedType, setSelectedType] = useState("none");
  const [comment, setComment] = useState("");
  // shiftData: 複数月を蓄積するフラットマップ dateKey ("YYYY-MM-DD") → { type, comment }
  //   タブ切替時にその月分のみマージ (既存の月データは保持)。
  //   保存時は年月単位でグループ化し、月ごとに DELETE+INSERT。
  const [shiftData, setShiftData] = useState({});
  // 既に DB から読み込んだ year_month の集合 (= 二重 fetch を防ぎ、未保存の変更を上書きしない)
  const [loadedMonths, setLoadedMonths] = useState(() => new Set());
  const [submitStatus, setSubmitStatus] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [animateSubmit, setAnimateSubmit] = useState(false);
  const [staffInfo, setStaffInfo] = useState(null);
  const [shiftPatterns, setShiftPatterns] = useState([]);
  const [requestGroups, setRequestGroups] = useState({});
  const [userSkillGroups, setUserSkillGroups] = useState([]);

  // recurring_events: dateKey -> [{ id, name, block_shift_request }]
  const [eventsByDate, setEventsByDate] = useState({});

  // 期間ベース受付の状態:
  //   groupId -> { label, periodLabel, maxCount, acquired, windowStart, windowEnd, displayMarks: Set<string> }
  // acquired は monthly_shifts から集計した過去取得数（対象月以前）
  const [periodStateByGroup, setPeriodStateByGroup] = useState({});

  // 選択中タブに自動スクロール (初期表示 + 月切替時)
  useEffect(() => {
    const container = tabsContainerRef.current;
    const active = activeTabRef.current;
    if (!container || !active) return;
    // タブ中央にくるように水平スクロール
    const cRect = container.getBoundingClientRect();
    const aRect = active.getBoundingClientRect();
    const offset = (aRect.left + aRect.width / 2) - (cRect.left + cRect.width / 2);
    container.scrollBy({ left: offset, behavior: 'smooth' });
  }, [targetMonth, availableMonths]);

  // deadlineDay が確定したら targetMonth を初期化し、閲覧可能月の一覧を作る
  // cycle 定義ありならロック前の最初の cycle の 期間終了月、無ければ legacy 計算
  useEffect(() => {
    if (deadlineDay === null) return;
    setAvailableMonths(buildAvailableMonths());
    setTargetMonth((prev) => {
      if (prev) return prev;
      if ((cycles || []).length > 0) {
        const now = Date.now();
        const openCycle = [...cycles]
          .filter((c) => new Date(c.submission_deadline).getTime() >= now)
          .sort((a, b) => a.submission_deadline.localeCompare(b.submission_deadline))[0];
        if (openCycle) {
          const end = parseLocalDate(openCycle.target_period_end);
          return new Date(end.getFullYear(), end.getMonth(), 1);
        }
      }
      return calcInitialDisplayMonth(deadlineDay);
    });
  }, [deadlineDay, cycles]);

  // 期間ベース受付の取得済みカウント
  // - 各 group の holidayPeriodLimits を提出対象月にマッチング
  // - マッチした期間内（提出対象月より前）の monthly_shifts から取得済み数を集計
  useEffect(() => {
    const calc = async () => {
      if (!targetMonth || !staffInfo?.employeeId || !currentOrganization?.id) return;
      const groupEntries = Object.entries(requestGroups);
      if (groupEntries.length === 0) {
        setPeriodStateByGroup({});
        return;
      }

      const targetY = targetMonth.getFullYear();
      const targetM = targetMonth.getMonth() + 1;

      // スタッフ所属に応じた「実効上限」を計算するヘルパー
      //   maxCountByGroup に自分の所属グループがあれば最小値（最も厳しい）、
      //   どのグループにも該当しなければ基本 maxCount。
      const computeEffectiveMaxCount = (pl) => {
        const base = Number(pl.maxCount);
        const byGroup = Array.isArray(pl.maxCountByGroup) ? pl.maxCountByGroup : [];
        if (byGroup.length === 0) return base;
        const myMatches = byGroup
          .filter(b => userSkillGroups.some(ug => ug.id === b.skillGroupId))
          .map(b => Number(b.maxCount))
          .filter(n => Number.isFinite(n));
        if (myMatches.length === 0) return base;
        return Math.min(...myMatches);
      };

      // 各 group について、対象月にマッチする period を 1 件だけ採用（実効上限の小さい方を優先）
      const matched = {};
      for (const [groupId, group] of groupEntries) {
        const periods = group.holidayPeriodLimits || [];
        if (periods.length === 0) continue;
        let chosen = null;
        let chosenWin = null;
        let chosenEff = null;
        for (const pl of periods) {
          const win = resolvePeriodWindow(pl.startMonth, pl.endMonth, targetY, targetM);
          if (!win) continue;
          const eff = computeEffectiveMaxCount(pl);
          if (!chosen || eff < chosenEff) {
            chosen = pl;
            chosenWin = win;
            chosenEff = eff;
          }
        }
        if (chosen && chosenWin) {
          matched[groupId] = { period: chosen, window: chosenWin, group, effectiveMaxCount: chosenEff };
        }
      }

      // どの group も期間にマッチしなければ早期終了
      if (Object.keys(matched).length === 0) {
        setPeriodStateByGroup({});
        return;
      }

      // 期間ウィンドウのうち、最も広い範囲をひとまとめにクエリする
      let minStart = null;
      let maxEnd = null;
      Object.values(matched).forEach(({ window: w }) => {
        const { start, end } = periodWindowToDateRange(w);
        if (!minStart || start < minStart) minStart = start;
        if (!maxEnd || end > maxEnd) maxEnd = end;
      });

      // 提出対象月の前月末まで（提出対象月分はまだ確定していないので除外）
      const targetMonthStart = `${targetY}-${String(targetM).padStart(2, '0')}-01`;

      try {
        const { data: shifts, error } = await supabase
          .from('monthly_shifts')
          .select('shift_date, shift_type')
          .eq('organization_id', currentOrganization.id)
          .eq('employee_id', staffInfo.employeeId)
          .gte('shift_date', minStart)
          .lte('shift_date', maxEnd)
          .lt('shift_date', targetMonthStart);
        if (error) throw error;

        const rows = shifts || [];
        const next = {};
        Object.entries(matched).forEach(([groupId, { period, window: w, group, effectiveMaxCount }]) => {
          const { start, end } = periodWindowToDateRange(w);
          const marks = new Set((group.targetDisplayMarks || []).map(m => String(m).trim()));
          let acquired = 0;
          rows.forEach(row => {
            if (!row.shift_date || !row.shift_type) return;
            if (row.shift_date < start || row.shift_date > end) return;
            if (row.shift_date >= targetMonthStart) return;
            if (marks.has(String(row.shift_type).trim())) acquired++;
          });
          // maxCount は effective（自分の所属に応じた値）、baseMaxCount は基本値（UI 補助）
          const eff = Number.isFinite(effectiveMaxCount) ? effectiveMaxCount : Number(period.maxCount);
          next[groupId] = {
            periodLabel: period.label || '',
            periodId: period.id,
            maxCount: eff,
            baseMaxCount: Number(period.maxCount),
            hasGroupOverride: Array.isArray(period.maxCountByGroup) && period.maxCountByGroup.length > 0,
            acquired,
            windowStart: start,
            windowEnd: end,
            startMonth: period.startMonth,
            endMonth: period.endMonth,
          };
        });
        setPeriodStateByGroup(next);
      } catch (e) {
        console.warn('期間ベース取得数の集計に失敗（継続）:', e.message);
        setPeriodStateByGroup({});
      }
    };
    calc();
  }, [targetMonth, requestGroups, staffInfo?.employeeId, currentOrganization?.id, userSkillGroups]);

  // ✅ 期間判定:
  //   - cycle (shift_submission_cycles) に該当: その cycle の 対象期間 + 締切
  //   - cycle 無し + 未来月        : 「自由入力モード」= 期間はカレンダー月、締切なし
  //   - 過去月                     : 閲覧専用 (= isViewOnly)
  const resolvedPeriod = targetMonth
    ? resolvePeriodForMonth(targetMonth, cycles, deadlineDay)
    : null;
  const isViewOnly = isPastMonth(targetMonth);           // 過去月 = 閲覧専用
  const isPastDeadline = isLocked(resolvedPeriod);        // cycle 締切後 = ロック (変更希望のみ)
  const hasCycle = !!resolvedPeriod && resolvedPeriod.kind === 'cycle';
  const deadlineDate = resolvedPeriod?.deadline || null;
  const deadlineDateText = deadlineDate ? formatDeadlineText(deadlineDate) : '';

  useEffect(() => {
    if (isLoading || !currentUser || !currentOrganization) return;

    const loadData = async () => {
      try {
        const organizationId = currentOrganization.id;
        if (!organizationId) { alert("組織情報が正しく読み込まれていません。"); return; }

        const userEmail = currentUser.email || currentUser.user_metadata?.email;
        if (!userEmail) { alert("ユーザー情報が正しく読み込まれていません。"); return; }

        // ✅ 締め切り日を取得 → deadlineDay をセット → targetMonth が自動計算される
        const { data: policyData } = await supabase
          .from('holiday_policies')
          .select('submission_deadline_day')
          .eq('organization_id', organizationId)
          .single();
        const fetchedDeadlineDay = policyData?.submission_deadline_day ?? 10;
        setDeadlineDay(fetchedDeadlineDay);

        // サイクル (shift_submission_cycles) を取得。 1 件以上あれば cycle 優先。
        const { data: cyclesData } = await supabase
          .from('shift_submission_cycles')
          .select('*')
          .eq('organization_id', organizationId)
          .order('submission_deadline', { ascending: true });
        setCycles(cyclesData || []);

        // targetMonth はこの時点ではまだ state に反映されていないため、
        // ローカル変数で計算して以降の処理に使う (= 初回表示月)
        const resolvedTargetMonth = calcInitialDisplayMonth(fetchedDeadlineDay);

        // スタッフ情報取得
        const { data: staffData, error: staffError } = await supabase
          .from('staff')
          .select('*')
          .eq('organization_id', organizationId)
          .eq('email', userEmail)
          .eq('is_active', true)
          .single();
        
        if (staffError) {
          if (staffError.code !== 'PGRST116') throw staffError;
          alert("スタッフ情報が見つかりません。管理者に連絡してください。");
          return;
        }
        
        if (staffData) {
          setStaffInfo({
            id: staffData.id,
            employeeId: staffData.employee_id,
            lastName: staffData.last_name,
            firstName: staffData.first_name,
            email: staffData.email
          });
        }

        // 勤務パターン取得
        const { data: patternsData, error: patternsError } = await supabase
          .from('shift_patterns')
          .select('*')
          .eq('organization_id', organizationId)
          .eq('is_active', true)
          .order('display_order', { ascending: true });
        
        if (patternsError) throw patternsError;
        
        const patterns = (patternsData || []).map(p => {
          const config = p.configuration || {};
          return {
            id: p.id,
            name: p.name,
            displayMark: p.short_name || p.display_mark,
            canReceiveRequest: config.can_receive_request || false,
            requestGroup: config.request_group || '',
            requestGroupLabel: config.request_group_label || '',
            requestDisplayMark: config.request_display_mark || '',
            requestColor: config.request_color || '#757575',
            requestPriority: config.request_priority || 999,
            maxMonthlyRequests: config.max_monthly_requests || null,
            maxMonthlyRequestsByGroup: config.max_monthly_requests_by_group || [],
            requestRuleComment: config.request_rule_comment || '',
            holidayPeriodLimits: Array.isArray(config.holiday_period_limits) ? config.holiday_period_limits : [],
          };
        });
        
        setShiftPatterns(patterns);

        const groups = {};
        patterns
          .filter(p => p.canReceiveRequest && p.requestGroup)
          .forEach(p => {
            const groupId = p.requestGroup;
            if (!groups[groupId]) {
              groups[groupId] = {
                id: groupId,
                label: p.requestGroupLabel || p.name,
                displayMark: p.requestDisplayMark || p.displayMark,
                color: p.requestColor || "#757575",
                priority: p.requestPriority || 999,
                maxMonthlyRequests: p.maxMonthlyRequests,
                maxMonthlyRequestsByGroup: [],
                requestRuleComment: p.requestRuleComment || '',
                targetPatternIds: [],
                targetDisplayMarks: [],      // monthly_shifts カウント用
                holidayPeriodLimits: [],     // 期間ベース上限の集約
              };
            }
            groups[groupId].targetPatternIds.push(p.id);
            if (p.displayMark) groups[groupId].targetDisplayMarks.push(p.displayMark);
            if (p.holidayPeriodLimits && p.holidayPeriodLimits.length > 0) {
              p.holidayPeriodLimits.forEach(pl => {
                // 同一 id が既に登録済みならスキップ
                if (!groups[groupId].holidayPeriodLimits.some(x => x.id === pl.id)) {
                  groups[groupId].holidayPeriodLimits.push(pl);
                }
              });
            }
            if (p.maxMonthlyRequestsByGroup && p.maxMonthlyRequestsByGroup.length > 0) {
              p.maxMonthlyRequestsByGroup.forEach(item => {
                const existing = groups[groupId].maxMonthlyRequestsByGroup.find(
                  e => e.skillGroupId === item.skillGroupId
                );
                if (!existing) {
                  groups[groupId].maxMonthlyRequestsByGroup.push({ ...item });
                } else if (item.maxRequests > existing.maxRequests) {
                  existing.maxRequests = item.maxRequests;
                  if (item.comment) existing.comment = item.comment;
                }
              });
            }
            // 同一 request_group 内で、各パターンに max_monthly_requests が設定されている場合は MAX を採用。
            // (例: 公休-ロ1=10, 公休-ロ2=10 → グループ上限 10)
            // ※ MIN にすると「古い設定値のパターンに引っ張られて設定より少なく制限される」バグが起きるため MAX に統一。
            if (p.maxMonthlyRequests) {
              if (!groups[groupId].maxMonthlyRequests ||
                  p.maxMonthlyRequests > groups[groupId].maxMonthlyRequests) {
                groups[groupId].maxMonthlyRequests = p.maxMonthlyRequests;
              }
            }
            if (p.requestRuleComment && !groups[groupId].requestRuleComment) {
              groups[groupId].requestRuleComment = p.requestRuleComment;
            }
          });

        setRequestGroups(groups);
        // shift_requests は targetMonth 変化で reload する別 effect で読み込む
        void resolvedTargetMonth;

      } catch (error) {
        console.error("❌ 情報取得エラー:", error);
        alert("データの読み込みに失敗しました: " + error.message);
      }
    };

    loadData();
  }, [currentUser, currentOrganization, isLoading]);

  // targetMonth が変わるたびに、そのカレンダー月分だけを DB から読み込んで既存 shiftData にマージ。
  // 既に読込済 (loadedMonths に含まれる) 月は再 fetch しない (= 未保存の変更を上書きしない)。
  useEffect(() => {
    const loadRequests = async () => {
      if (!targetMonth || !staffInfo?.employeeId || !currentOrganization?.id) return;
      const yearMonth = yearMonthForMonth(targetMonth);
      if (!yearMonth) return;
      if (loadedMonths.has(yearMonth)) return;  // キャッシュ済、再読込しない
      try {
        const { data: requests, error } = await supabase
          .from('shift_requests')
          .select('*')
          .eq('organization_id', currentOrganization.id)
          .eq('employee_id', staffInfo.employeeId)
          .eq('year_month', yearMonth);
        if (error) throw error;
        const monthData = {};
        (requests || []).forEach(req => {
          monthData[req.shift_date] = {
            type: req.selection,
            comment: req.comment || "",
          };
        });
        setShiftData(prev => ({ ...prev, ...monthData }));
        setLoadedMonths(prev => {
          const next = new Set(prev);
          next.add(yearMonth);
          return next;
        });
      } catch (e) {
        console.warn('shift_requests 取得失敗 (継続):', e.message);
      }
    };
    loadRequests();
  }, [targetMonth, staffInfo?.employeeId, currentOrganization?.id, loadedMonths]);

  // スタッフ/組織が変わったら全キャッシュ破棄
  useEffect(() => {
    setShiftData({});
    setLoadedMonths(new Set());
  }, [staffInfo?.employeeId, currentOrganization?.id]);

  useEffect(() => {
    const fetchUserGroups = async () => {
      if (!staffInfo?.employeeId || !currentOrganization?.id) return;
      try {
        const { data: groups, error } = await supabase
          .from('skill_groups')
          .select('*')
          .eq('organization_id', currentOrganization.id);
        if (error || !groups) return;
        // skill_groups.members は employee_id ベースで保存されている。
        // 古いデータが UUID (staff.id) で入っているケースの保険として両対応。
        const userGroups = groups.filter(g => {
          const members = typeof g.members === 'string'
            ? JSON.parse(g.members)
            : (g.members || []);
          return members.includes(staffInfo.employeeId)
              || members.includes(staffInfo.id);
        });
        setUserSkillGroups(userGroups);
      } catch (error) {
        console.error("❌ グループ取得エラー:", error);
      }
    };
    fetchUserGroups();
  }, [staffInfo, currentOrganization]);

  useEffect(() => {
    if (!targetMonth) return;
    // カレンダーグリッドは常にカレンダー月全体 (1日〜末日)。
    // cycle は締切/ロック判定のみに使用し、UI 的なマス表示は壊さない。
    const y = targetMonth.getFullYear();
    const m = targetMonth.getMonth();
    const start = new Date(y, m, 1);
    const end = new Date(y, m + 1, 0);
    const firstDayOfWeek = start.getDay();
    const days = [];
    for (let i = 0; i < firstDayOfWeek; i++) days.push(null);
    const cur = new Date(start);
    while (cur <= end) {
      days.push(new Date(cur));
      cur.setDate(cur.getDate() + 1);
    }
    setCalendarData(days);
  }, [targetMonth]);

  // ===== recurring_events を取得して対象日マップを作る =====
  useEffect(() => {
    const fetch = async () => {
      if (!currentOrganization?.id || !staffInfo?.id || !targetMonth) return;
      // カレンダー月の 1日〜末日 でイベントを取得
      const y = targetMonth.getFullYear();
      const m = targetMonth.getMonth();
      const toYmd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const monthStart = toYmd(new Date(y, m, 1));
      const monthEnd = toYmd(new Date(y, m + 1, 0));

      try {
        // 期間が対象月と重なるイベントを取得
        //  (開始 <= 月末) かつ (終了 >= 月初 or 終了が null で開始 >= 月初)
        //  簡単化のため広めに取ってクライアント側でフィルタ
        const { data, error } = await supabase
          .from('recurring_events')
          .select('id, name, event_date, event_end_date, event_dates, target_skill_group, target_staff_ids, block_shift_request')
          .eq('organization_id', currentOrganization.id)
          .lte('event_date', monthEnd);
        if (error) {
          console.warn('recurring_events 取得エラー:', error.message);
          return;
        }
        const myGroupNames = userSkillGroups.map((g) => g.name);
        const relevant = (data || []).filter((ev) => {
          // 対象判定: target_staff_ids に含まれる or 自分の skill group と一致
          const sids = Array.isArray(ev.target_staff_ids) ? ev.target_staff_ids : null;
          if (sids && sids.includes(staffInfo.id)) return true;
          if (ev.target_skill_group && myGroupNames.includes(ev.target_skill_group)) return true;
          return false;
        }).filter((ev) => {
          // 月と重なるものに限定 (複数日付モード対応)
          if (Array.isArray(ev.event_dates) && ev.event_dates.length > 0) {
            return ev.event_dates.some((d) => d >= monthStart && d <= monthEnd);
          }
          const end = ev.event_end_date || ev.event_date;
          return end >= monthStart && ev.event_date <= monthEnd;
        });
        // イベント対象日を日ごとに展開
        const map = {};
        const pushDay = (dateKey, ev) => {
          if (!map[dateKey]) map[dateKey] = [];
          map[dateKey].push({
            id: ev.id,
            name: ev.name,
            block_shift_request: !!ev.block_shift_request,
          });
        };
        const inPeriod = (d) => {
          const key = formatDateKey(d);
          return key >= monthStart && key <= monthEnd;
        };
        relevant.forEach((ev) => {
          if (Array.isArray(ev.event_dates) && ev.event_dates.length > 0) {
            ev.event_dates.forEach((dStr) => {
              const d = new Date(dStr + 'T00:00:00');
              if (!inPeriod(d)) return;
              pushDay(formatDateKey(d), ev);
            });
          } else {
            const start = new Date(ev.event_date + 'T00:00:00');
            const end = new Date((ev.event_end_date || ev.event_date) + 'T00:00:00');
            for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
              if (!inPeriod(d)) continue;
              pushDay(formatDateKey(d), ev);
            }
          }
        });
        setEventsByDate(map);
      } catch (e) {
        console.error('イベント取得失敗:', e);
      }
    };
    fetch();
  }, [currentOrganization?.id, staffInfo?.id, targetMonth, userSkillGroups]);

  // ローディング中
  if (isLoading || deadlineDay === null || targetMonth === null) {
    return (
      <div style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        display: "flex", alignItems: "center", justifyContent: "center",
        flexDirection: "column", gap: "1rem"
      }}>
        <Loader2 size={48} color="white" style={{animation: "spin 1s linear infinite"}} />
        <p style={{ color: "white", fontSize: "1.2rem" }}>読み込み中...</p>
        <style>{`@keyframes spin { from{transform:rotate(0deg)}to{transform:rotate(360deg)} }`}</style>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: "2rem"
      }}>
        <div style={{ background: "white", borderRadius: "16px", padding: "2rem", textAlign: "center", maxWidth: "400px" }}>
          <AlertCircle size={48} style={{ color: "#F44336", marginBottom: "1rem" }} />
          <h2 style={{ marginBottom: "1rem" }}>認証情報が取得できません</h2>
          <p style={{ color: "#666", marginBottom: "1rem" }}>ログインし直してください。</p>
          <button onClick={() => window.location.href = '/'} style={{ padding: "0.75rem 1.5rem", background: "#667eea", color: "white", border: "none", borderRadius: "8px", cursor: "pointer" }}>
            ログインページへ
          </button>
        </div>
      </div>
    );
  }

  if (!currentOrganization) {
    return (
      <div style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: "2rem"
      }}>
        <div style={{ background: "white", borderRadius: "16px", padding: "2rem", textAlign: "center", maxWidth: "400px" }}>
          <AlertCircle size={48} style={{ color: "#F44336", marginBottom: "1rem" }} />
          <h2 style={{ marginBottom: "1rem" }}>組織情報を読み込み中...</h2>
          <p style={{ color: "#666" }}>しばらくお待ちください。</p>
        </div>
      </div>
    );
  }

  const handleDateClick = (date) => {
    if (isViewOnly) {
      // 過去月: 閲覧専用
      return;
    }
    if (isPastDeadline) {
      // cycle 締切後: 変更希望モーダルを開く
      openChangeRequestModal(date);
      return;
    }
    const key = formatDateKey(date);
    const events = eventsByDate[key] || [];

    // 強制配置がある場合: 希望入力を拒否
    const blocked = events.find((e) => e.block_shift_request);
    if (blocked) {
      alert(
        `この日は「${blocked.name}」で強制配置が設定されています。\n\n希望を提出することはできません (管理者の設定により)。`
      );
      return;
    }
    // それ以外のイベントがある場合: 警告だけ出し、OK なら入力継続
    if (events.length > 0) {
      const names = events.map((e) => `・${e.name}`).join('\n');
      const ok = window.confirm(
        `この日は以下のイベントが設定されています:\n${names}\n\n希望を入力しますか？`
      );
      if (!ok) return;
    }

    setSelectedDate(date);
    setSelectedType(shiftData[key]?.type || "none");
    setComment(shiftData[key]?.comment || "");
  };

  const handleModalSubmit = () => {
    if (!selectedDate) return;
    const key = formatDateKey(selectedDate);
    const newData = { ...shiftData };
    if (selectedType === "none") {
      delete newData[key];
    } else {
      newData[key] = { type: selectedType, comment };
    }
    // 月単位の希望回数制限: shiftData は全月分蓄積されているので、
    // 現在編集中の月 (= targetMonth のカレンダー月) 分だけでカウント。
    const currentYearMonth = yearMonthForMonth(targetMonth);
    const countByGroup = {};
    Object.entries(newData).forEach(([dateKey, v]) => {
      if (dateKey.substring(0, 7) !== currentYearMonth) return;
      countByGroup[v.type] = (countByGroup[v.type] || 0) + 1;
    });
    for (const [groupId, count] of Object.entries(countByGroup)) {
      const group = requestGroups[groupId];
      let effectiveLimit = group.maxMonthlyRequests;
      if (group.maxMonthlyRequestsByGroup && group.maxMonthlyRequestsByGroup.length > 0) {
        const userLimits = group.maxMonthlyRequestsByGroup
          .filter(item => userSkillGroups.some(ug => ug.id === item.skillGroupId))
          .map(item => item.maxRequests);
        if (userLimits.length > 0) effectiveLimit = Math.max(...userLimits);
      }
      if (effectiveLimit && count > effectiveLimit) {
        alert(`${group.label}は月${effectiveLimit}回までです。\n現在: ${count}回選択されています。`);
        return;
      }
      // 期間ベース上限（過去取得 + 今回選択 が maxCount を超えるか）
      const ps = periodStateByGroup[groupId];
      if (ps && (ps.acquired + count) > ps.maxCount) {
        const periodTxt = `${ps.startMonth}〜${ps.endMonth}月` + (ps.periodLabel ? `（${ps.periodLabel}）` : '');
        alert(
          `${group.label}は${periodTxt}で合計${ps.maxCount}回までです。\n` +
          `これまでに${ps.acquired}回取得済みのため、今回追加できるのは残り ${Math.max(0, ps.maxCount - ps.acquired)} 回です。\n` +
          `（現在: ${count}回選択中）`
        );
        return;
      }
    }
    setShiftData(newData);
    setSelectedDate(null);
  };

  const handleSubmit = async () => {
    if (!currentUser) { alert("ログインが必要です"); return; }
    if (!staffInfo?.employeeId) { alert("スタッフ情報が正しく読み込まれていません。"); return; }
    if (!currentOrganization) { alert("組織情報が読み込まれていません。"); return; }

    // 保存対象の月を loadedMonths から選別:
    //   - 過去月 (閲覧専用) はスキップ
    //   - cycle 締切後 (ロック済) はスキップ
    //   - それ以外 (= 編集可能だった月) を保存対象に
    const savableMonths = [];
    const skippedLocked = [];
    for (const ym of loadedMonths) {
      const [y, m] = ym.split('-').map(Number);
      const monthDate = new Date(y, m - 1, 1);
      if (isPastMonth(monthDate)) continue;  // 過去月
      const period = resolvePeriodForMonth(monthDate, cycles, deadlineDay);
      if (isLocked(period)) {
        skippedLocked.push(ym);
        continue;
      }
      savableMonths.push(ym);
    }

    if (savableMonths.length === 0) {
      alert('保存可能な月がありません (全て締切済み or 対象外です)。');
      return;
    }

    setSubmitting(true);
    try {
      const organizationId = currentOrganization.id;
      let totalInserted = 0;
      const savedMonths = [];
      for (const ym of savableMonths) {
        // この月の既存を全削除
        const { error: deleteError } = await supabase
          .from('shift_requests')
          .delete()
          .eq('organization_id', organizationId)
          .eq('employee_id', staffInfo.employeeId)
          .eq('year_month', ym);
        if (deleteError) throw deleteError;

        // shiftData からこの月のエントリを集めて INSERT
        const monthRecords = Object.entries(shiftData)
          .filter(([date]) => date.substring(0, 7) === ym)
          .map(([date, v]) => ({
            organization_id: organizationId,
            employee_id: staffInfo.employeeId,
            year_month: ym,
            shift_date: date,
            selection: v.type,
            comment: v.comment || "",
            submitted_at: new Date().toISOString(),
          }));

        if (monthRecords.length > 0) {
          const { error: insertError } = await supabase
            .from('shift_requests')
            .insert(monthRecords);
          if (insertError) throw insertError;
          totalInserted += monthRecords.length;
        }
        savedMonths.push(ym);
      }

      await writeClientAudit({
        organizationId,
        actionType: 'update',
        targetTable: 'shift_requests',
        targetSummary: `シフト希望を一括提出: ${savedMonths.join(', ')} (${totalInserted} 日分, employeeId=${staffInfo.employeeId})`,
        changes: { months: savedMonths, totalDays: totalInserted, employee_id: staffInfo.employeeId },
        success: true,
      });

      const skipMsg = skippedLocked.length > 0
        ? ` (※ ${skippedLocked.join(', ')} は締切済みのため保存対象外)`
        : '';
      setSubmitStatus(`${savedMonths.length} ヶ月分の送信が完了しました！${skipMsg}`);
      setAnimateSubmit(true);
      setTimeout(() => setAnimateSubmit(false), 3000);
    } catch (error) {
      console.error("❌ 送信エラー:", error);
      await writeClientAudit({
        organizationId: currentOrganization?.id,
        actionType: 'update',
        targetTable: 'shift_requests',
        targetSummary: `シフト希望の一括提出に失敗 (employeeId=${staffInfo?.employeeId || 'unknown'})`,
        success: false,
        errorMessage: error.message,
      });
      setSubmitStatus("送信に失敗しました。もう一度お試しください。");
    } finally {
      setSubmitting(false);
    }
  };

  // 変更希望モーダルを開く
  const openChangeRequestModal = (date) => {
    setChangeRequestDate(date);
    setChangeRequestNote("");
    setChangeRequestDesired("");
    setChangeRequestStatus("");
    setChangeRequestOpen(true);
  };

  // 変更希望を送信
  const submitChangeRequest = async () => {
    if (!currentOrganization || !staffInfo?.employeeId) {
      alert("ユーザー情報が読み込まれていません。");
      return;
    }
    if (!changeRequestNote.trim()) {
      alert("変更理由を入力してください。");
      return;
    }
    setChangeRequestSubmitting(true);
    try {
      // year_month は常にタブ (= カレンダー月) ベース
      const yearMonth = yearMonthForMonth(targetMonth);
      const shiftDateStr = changeRequestDate ? formatDateKey(changeRequestDate) : null;
      const currentSelection = shiftDateStr ? (shiftData[shiftDateStr]?.type || null) : null;
      const { error } = await supabase
        .from('shift_change_requests')
        .insert({
          organization_id: currentOrganization.id,
          employee_id: staffInfo.employeeId,
          requested_by_staff_id: staffInfo.id || null,
          year_month: yearMonth,
          shift_date: shiftDateStr,
          current_selection: currentSelection,
          requested_selection: changeRequestDesired || null,
          note: changeRequestNote.trim(),
          status: 'unread',
        });
      if (error) throw error;
      await writeClientAudit({
        organizationId: currentOrganization.id,
        actionType: 'create',
        targetTable: 'shift_change_requests',
        targetSummary: `変更希望を送信: ${yearMonth}${shiftDateStr ? ` (${shiftDateStr})` : ''}`,
        changes: { yearMonth, shift_date: shiftDateStr, employee_id: staffInfo.employeeId },
        success: true,
      });
      setChangeRequestStatus('ok');
      setTimeout(() => {
        setChangeRequestOpen(false);
        setChangeRequestStatus("");
      }, 1200);
    } catch (e) {
      console.error("❌ 変更希望の送信に失敗:", e);
      await writeClientAudit({
        organizationId: currentOrganization?.id,
        actionType: 'create',
        targetTable: 'shift_change_requests',
        targetSummary: `変更希望の送信に失敗 (employeeId=${staffInfo?.employeeId})`,
        success: false,
        errorMessage: e.message,
      });
      setChangeRequestStatus('error');
    } finally {
      setChangeRequestSubmitting(false);
    }
  };

  const formatDateShort = (date) => {
    const dayNames = ["日", "月", "火", "水", "木", "金", "土"];
    return `${date.getMonth() + 1}/${date.getDate()}(${dayNames[date.getDay()]})`;
  };

  // 画面表示用: 現在タブのカレンダー月のみを集計 (shiftData は全月蓄積)
  const currentYearMonthKey = yearMonthForMonth(targetMonth);
  const countByGroup = {};
  Object.entries(shiftData).forEach(([dateKey, v]) => {
    if (currentYearMonthKey && dateKey.substring(0, 7) !== currentYearMonthKey) return;
    countByGroup[v.type] = (countByGroup[v.type] || 0) + 1;
  });

  // 一括送信時に実際に保存される月数 (= 編集可能な loadedMonths)
  const savableMonthsCount = (() => {
    let n = 0;
    loadedMonths.forEach((ym) => {
      const [y, m] = ym.split('-').map(Number);
      const d = new Date(y, m - 1, 1);
      if (isPastMonth(d)) return;
      const p = resolvePeriodForMonth(d, cycles, deadlineDay);
      if (isLocked(p)) return;
      n++;
    });
    return n;
  })();

  // 期間ベース受付の制御:
  //   - holidayPeriodLimits を持つ group は、対象月が期間内かどうかで「表示／非表示」を決める
  //   - periodStateByGroup にマッチがあれば期間内、なければ期間外（非表示）
  const isGroupAvailableThisMonth = (group) => {
    const hasPeriod = (group.holidayPeriodLimits || []).length > 0;
    if (!hasPeriod) return true; // 期間設定がない group は常に表示
    return !!periodStateByGroup[group.id];
  };

  const sortedRequestGroups = Object.values(requestGroups)
    .filter(isGroupAvailableThisMonth)
    .sort((a, b) => a.priority - b.priority);

  const requestOptions = [
    { value: "none", label: "希望なし", color: "#757575", displayMark: "-" },
    ...sortedRequestGroups.map(g => ({ value: g.id, label: g.label, color: g.color, displayMark: g.displayMark }))
  ];

  const getColorForType = (type) => {
    if (type === "none") return "#F5F5F5";
    const group = requestGroups[type];
    return group ? `${group.color}20` : "#F5F5F5";
  };

  const getBorderColorForType = (type) => {
    if (type === "none") return "#ddd";
    const group = requestGroups[type];
    return group ? group.color : "#ddd";
  };

  const getEffectiveComment = (group) => {
    if (group.maxMonthlyRequestsByGroup && group.maxMonthlyRequestsByGroup.length > 0) {
      const matchingGroupSettings = group.maxMonthlyRequestsByGroup
        .filter(item => userSkillGroups.some(ug => ug.id === item.skillGroupId));
      const commentsFromGroups = matchingGroupSettings
        .filter(item => item.comment && item.comment.trim() !== '')
        .map(item => item.comment);
      if (commentsFromGroups.length > 0) return commentsFromGroups.join(' / ');
    }
    return group.requestRuleComment || '';
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      padding: "1rem",
      paddingBottom: "6rem",
    }}>
      <div style={{ maxWidth: "800px", margin: "0 auto", animation: "fadeIn 0.5s ease" }}>

        <div style={{
          background: "white",
          borderRadius: "16px",
          padding: "1rem",
          boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
          marginBottom: "1.5rem",
          animation: "slideUp 0.6s ease",
        }}>
          <div style={{ 
            display: "flex", alignItems: "center", gap: "12px",
            marginBottom: "1rem", paddingBottom: "1rem", borderBottom: "2px solid #f0f0f0"
          }}>
            <Calendar size={28} style={{ color: "#667eea" }} />
            <div style={{ flex: 1 }}>
              <h2 style={{
                margin: 0, fontSize: "1.5rem", fontWeight: "700",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
              }}>
                {targetMonth.getFullYear()}年{targetMonth.getMonth() + 1}月
              </h2>
              <p style={{ margin: "4px 0 0 0", fontSize: "0.85rem", color: "#666" }}>
                シフト希望提出フォーム
              </p>
              {isViewOnly ? (
                <div style={{
                  display: "inline-flex", alignItems: "center", gap: "4px",
                  marginTop: "6px", padding: "3px 10px", borderRadius: "20px",
                  fontSize: "0.8rem", fontWeight: "600",
                  background: "#f3f4f6", color: "#6b7280",
                }}>
                  <Info size={12} />
                  過去月 (閲覧専用)
                </div>
              ) : hasCycle ? (
                <>
                  <div style={{
                    display: "inline-flex", alignItems: "center", gap: "4px",
                    marginTop: "6px", marginRight: "6px",
                    padding: "3px 10px", borderRadius: "20px",
                    fontSize: "0.8rem", fontWeight: "600",
                    background: "#eef2ff", color: "#4338ca",
                  }}>
                    <Calendar size={12} />
                    対象期間: {formatPeriodShort(resolvedPeriod)}
                    {resolvedPeriod.cycle?.name && ` (${resolvedPeriod.cycle.name})`}
                  </div>
                  <div style={{
                    display: "inline-flex", alignItems: "center", gap: "4px",
                    marginTop: "6px", padding: "3px 10px", borderRadius: "20px",
                    fontSize: "0.8rem", fontWeight: "600",
                    background: isPastDeadline ? "#fee2e2" : "#fef3c7",
                    color: isPastDeadline ? "#dc2626" : "#92400e",
                  }}>
                    {isPastDeadline ? <Lock size={12} /> : <Info size={12} />}
                    {isPastDeadline
                      ? `締め切り済み（${deadlineDateText}）`
                      : `締め切り：${deadlineDateText}まで`
                    }
                  </div>
                </>
              ) : (
                <div style={{
                  display: "inline-flex", alignItems: "center", gap: "4px",
                  marginTop: "6px", padding: "3px 10px", borderRadius: "20px",
                  fontSize: "0.8rem", fontWeight: "600",
                  background: "#dcfce7", color: "#166534",
                }}>
                  <Info size={12} />
                  受付中 (締切未設定)
                </div>
              )}
            </div>
          </div>

          {/* 月切り替えタブ (過去 6 ヶ月〜1 年先) */}
          <div ref={tabsContainerRef} style={{
            display: "flex", flexWrap: "nowrap", gap: "4px",
            overflowX: "auto", padding: "4px 0 12px 0",
            borderBottom: "1px solid #f0f0f0", marginBottom: "12px",
            WebkitOverflowScrolling: "touch",
          }}>
            {availableMonths.map((m) => {
              const key = monthKey(m);
              const active = monthKey(targetMonth) === key;
              const tabPeriod = resolvePeriodForMonth(m, cycles, deadlineDay);
              const past = isPastMonth(m);
              const locked = !past && isLocked(tabPeriod);
              const openWithCycle = !past && !locked && !!tabPeriod && tabPeriod.kind === 'cycle';
              const openFree = !past && !locked && !openWithCycle;
              return (
                <button
                  key={key}
                  ref={active ? activeTabRef : null}
                  onClick={() => setTargetMonth(m)}
                  style={{
                    flex: "0 0 auto",
                    padding: "6px 10px",
                    borderRadius: "16px",
                    border: active ? "2px solid #667eea" : "1px solid #e5e7eb",
                    background: active
                      ? "#eef2ff"
                      : (past ? "#f9fafb" : "white"),
                    color: active
                      ? "#4338ca"
                      : (past ? "#9ca3af" : "#374151"),
                    fontWeight: active ? 700 : 500,
                    fontSize: "0.8rem",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                  title={
                    past ? "過去月 (閲覧のみ)"
                    : locked ? `締切済み: ${formatDeadlineText(tabPeriod.deadline)}`
                    : openWithCycle ? `対象: ${formatPeriodShort(tabPeriod)} / 締切: ${formatDeadlineText(tabPeriod.deadline)}`
                    : "受付中 (締切未設定)"
                  }
                >
                  {past && <Info size={10} />}
                  {locked && <Lock size={10} />}
                  {monthLabel(m)}
                  {openFree && !active && (
                    <span style={{
                      fontSize: '0.6rem', background: '#dcfce7', color: '#166534',
                      padding: '0 4px', borderRadius: 4, fontWeight: 700,
                    }}>自由</span>
                  )}
                </button>
              );
            })}
          </div>

          <div style={{
            display: "grid", gridTemplateColumns: "repeat(7, 1fr)",
            gap: "4px", width: "100%", boxSizing: "border-box",
          }}>
            {["日", "月", "火", "水", "木", "金", "土"].map((day, i) => (
              <div key={i} style={{
                textAlign: "center", fontWeight: "600",
                color: i === 0 ? "#F44336" : i === 6 ? "#2196F3" : "#666",
                fontSize: "0.85rem", padding: "8px 0",
              }}>
                {day}
              </div>
            ))}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "8px" }}>
            {calendarData.map((date, index) => {
              if (!date) return <div key={`empty-${index}`} />;
              const key = formatDateKey(date);
              const hasData = shiftData[key];
              const dayOfWeek = date.getDay();
              const group = hasData ? requestGroups[hasData.type] : null;
              const dayEvents = eventsByDate[key] || [];
              const hasBlockedEvent = dayEvents.some((e) => e.block_shift_request);
              const hasAnyEvent = dayEvents.length > 0;

              return (
                <button
                  key={index}
                  onClick={() => handleDateClick(date)}
                  disabled={isViewOnly}
                  style={{
                    aspectRatio: "1",
                    position: "relative",
                    border: hasData
                      ? `2px solid ${getBorderColorForType(hasData.type)}`
                      : hasBlockedEvent
                        ? "2px solid #dc2626"
                        : hasAnyEvent
                          ? "2px solid #f59e0b"
                          : "1px solid #ddd",
                    borderRadius: "8px",
                    backgroundColor: hasData
                      ? getColorForType(hasData.type)
                      : hasBlockedEvent
                        ? "#fef2f2"
                        : hasAnyEvent
                          ? "#fffbeb"
                          : "white",
                    cursor: isViewOnly ? "not-allowed" : "pointer",
                    transition: "all 0.2s ease",
                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                    fontSize: "0.85rem", fontWeight: hasData ? "600" : "400",
                    color: dayOfWeek === 0 ? "#F44336" : dayOfWeek === 6 ? "#2196F3" : "#333",
                    boxShadow: hasData ? "0 2px 8px rgba(102, 126, 234, 0.2)" : "none",
                    padding: "0", minWidth: "0", width: "100%", boxSizing: "border-box",
                    opacity: isViewOnly ? (hasData ? 0.85 : 0.55) : (isPastDeadline && !hasData ? 0.6 : 1),
                  }}
                  title={
                    isViewOnly ? "過去月 (閲覧専用)"
                    : isPastDeadline ? "締切済み: タップで変更希望を送信"
                    : (dayEvents.length > 0 ? dayEvents.map((e) => e.name).join(" / ") : undefined)
                  }
                  onMouseEnter={(e) => {
                    if (isViewOnly) return;
                    e.currentTarget.style.transform = "scale(1.05)";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(102, 126, 234, 0.3)";
                  }}
                  onMouseLeave={(e) => {
                    if (isViewOnly) return;
                    e.currentTarget.style.transform = "scale(1)";
                    e.currentTarget.style.boxShadow = hasData ? "0 2px 8px rgba(102, 126, 234, 0.2)" : "none";
                  }}
                >
                  <div>{date.getDate()}</div>
                  {hasData && group && (
                    <div style={{
                      fontSize: "0.65rem", marginTop: "2px", padding: "2px 4px",
                      borderRadius: "4px", backgroundColor: group.color, color: "white", fontWeight: "600",
                    }}>
                      {group.displayMark || "✓"}
                    </div>
                  )}
                  {!hasData && hasAnyEvent && (
                    <div style={{
                      fontSize: "0.55rem", marginTop: "2px", padding: "1px 4px",
                      borderRadius: "4px",
                      backgroundColor: hasBlockedEvent ? "#dc2626" : "#f59e0b",
                      color: "white", fontWeight: "700",
                      maxWidth: "90%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>
                      {hasBlockedEvent ? "強制" : "行事"}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div style={{
          background: "white", borderRadius: "16px", padding: "1.5rem",
          boxShadow: "0 8px 32px rgba(0,0,0,0.1)", marginBottom: "1.5rem",
          animation: "slideUp 0.7s ease",
        }}>
          {/* 期間ベース上限の取得状況カード（毎年繰り返しの「夏期5回」「年末年始3回」など） */}
          {sortedRequestGroups.filter(g => periodStateByGroup[g.id]).length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", marginBottom: "1rem", gap: "1rem" }}>
              {sortedRequestGroups.filter(g => periodStateByGroup[g.id]).map(group => {
                const ps = periodStateByGroup[group.id];
                const thisFormCount = countByGroup[group.id] || 0;
                const total = ps.acquired + thisFormCount;
                const isOver = total > ps.maxCount;
                const remaining = Math.max(0, ps.maxCount - total);
                return (
                  <div key={`period-${group.id}`} style={{
                    flex: "1 1 200px", padding: "1rem", borderRadius: "12px",
                    background: `linear-gradient(135deg, ${group.color}15 0%, ${group.color}35 100%)`,
                    textAlign: "center",
                    border: isOver ? "2px solid #F44336" : `1px solid ${group.color}`,
                    position: "relative",
                  }}>
                    <div style={{
                      position: "absolute", top: 6, right: 8,
                      fontSize: "0.6rem", color: "#7e22ce", background: "#f3e8ff",
                      padding: "0.1rem 0.4rem", borderRadius: "3px", fontWeight: 600,
                    }}>
                      🗓 {ps.startMonth}〜{ps.endMonth}月
                    </div>
                    <div style={{ fontSize: "0.75rem", color: group.color, marginBottom: "4px", fontWeight: "600", marginTop: "4px" }}>
                      {group.label}{ps.periodLabel ? `（${ps.periodLabel}）` : ''}
                    </div>
                    <div style={{ fontSize: "1.75rem", fontWeight: "700", color: isOver ? "#F44336" : "#333" }}>
                      {total} / {ps.maxCount}
                    </div>
                    <div style={{ fontSize: "0.7rem", color: "#666", marginTop: "2px" }}>
                      既に取得: {ps.acquired}回 / 今回: {thisFormCount}回
                    </div>
                    <div style={{ fontSize: "0.7rem", color: isOver ? "#F44336" : "#16a34a", fontWeight: 600, marginTop: "2px" }}>
                      {isOver ? "上限超過" : `残り ${remaining} 回`}
                    </div>
                    {ps.hasGroupOverride && ps.baseMaxCount != null && ps.baseMaxCount !== ps.maxCount && (
                      <div style={{ fontSize: "0.65rem", color: "#7c3aed", marginTop: "2px" }}>
                        スキルグループ別の上限（基本 {ps.baseMaxCount} 回）
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {sortedRequestGroups.filter(g => {
            if (g.maxMonthlyRequestsByGroup && g.maxMonthlyRequestsByGroup.length > 0) {
              const userLimits = g.maxMonthlyRequestsByGroup
                .filter(item => userSkillGroups.some(ug => ug.id === item.skillGroupId))
                .map(item => item.maxRequests);
              if (userLimits.length > 0) return true;
              return g.maxMonthlyRequests;
            }
            return g.maxMonthlyRequests;
          }).length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", marginBottom: "1rem", gap: "1rem" }}>
              {sortedRequestGroups
                .filter(g => {
                  if (g.maxMonthlyRequestsByGroup && g.maxMonthlyRequestsByGroup.length > 0) {
                    const userLimits = g.maxMonthlyRequestsByGroup
                      .filter(item => userSkillGroups.some(ug => ug.id === item.skillGroupId))
                      .map(item => item.maxRequests);
                    if (userLimits.length > 0) return true;
                    return g.maxMonthlyRequests;
                  }
                  return g.maxMonthlyRequests;
                })
                .map(group => {
                  const count = countByGroup[group.id] || 0;
                  let effectiveLimit = group.maxMonthlyRequests;
                  if (group.maxMonthlyRequestsByGroup && group.maxMonthlyRequestsByGroup.length > 0) {
                    const userLimits = group.maxMonthlyRequestsByGroup
                      .filter(item => userSkillGroups.some(ug => ug.id === item.skillGroupId))
                      .map(item => item.maxRequests);
                    if (userLimits.length > 0) effectiveLimit = Math.max(...userLimits);
                  }
                  const isOver = count > effectiveLimit;
                  return (
                    <div key={group.id} style={{
                      flex: "1 1 150px", padding: "1rem", borderRadius: "12px",
                      background: `linear-gradient(135deg, ${group.color}20 0%, ${group.color}40 100%)`,
                      textAlign: "center",
                      border: isOver ? "2px solid #F44336" : `1px solid ${group.color}`,
                    }}>
                      <div style={{ fontSize: "0.75rem", color: group.color, marginBottom: "4px", fontWeight: "600" }}>
                        {group.label}
                      </div>
                      <div style={{ fontSize: "1.75rem", fontWeight: "700", color: isOver ? "#F44336" : "#333" }}>
                        {count} / {effectiveLimit}
                      </div>
                    </div>
                  );
                })}
            </div>
          )}

          {isViewOnly ? (
            <div style={{
              padding: '0.85rem 1rem', background: '#f3f4f6', borderRadius: 12,
              textAlign: 'center', color: '#6b7280', fontSize: '0.85rem',
            }}>
              <Info size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              過去月のため閲覧専用です (提出済みの希望のみ表示)
            </div>
          ) : isPastDeadline ? (
            <button
              onClick={() => openChangeRequestModal(null)}
              disabled={!staffInfo}
              style={{
                width: "100%", padding: "1rem",
                background: staffInfo ? "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)" : "#9e9e9e",
                color: "white", border: "none", borderRadius: "28px",
                fontWeight: "600", fontSize: "1rem",
                cursor: staffInfo ? "pointer" : "not-allowed",
                boxShadow: staffInfo ? "0 4px 12px rgba(245, 158, 11, 0.3)" : "none",
                transition: "all 0.3s ease",
                display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
              }}
              title="この月は締切済みです。変更したい場合は管理者に通知を送れます。"
            >
              <MessageSquare size={18} />変更希望を管理者に送る
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitting || animateSubmit || !staffInfo}
              style={{
                width: "100%", padding: "1rem",
                background: animateSubmit
                  ? "linear-gradient(135deg, #4CAF50 0%, #45a049 100%)"
                  : (staffInfo ? "linear-gradient(135deg, #2196F3 0%, #1976D2 100%)" : "#9e9e9e"),
                color: "white", border: "none", borderRadius: "28px",
                fontWeight: "600", fontSize: "1rem",
                cursor: (submitting || !staffInfo) ? "not-allowed" : "pointer",
                boxShadow: !staffInfo ? "none" : "0 4px 12px rgba(33, 150, 243, 0.3)",
                transition: "all 0.3s ease",
                display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
              }}
              onMouseEnter={(e) => {
                if (!submitting && !animateSubmit && staffInfo) {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 6px 16px rgba(33, 150, 243, 0.4)";
                }
              }}
              onMouseLeave={(e) => {
                if (!submitting && !animateSubmit && staffInfo) {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 4px 12px rgba(33, 150, 243, 0.3)";
                }
              }}
            >
              {submitting ? (
                <><Loader2 size={18} style={{animation: "spin 1s linear infinite"}} />送信中...</>
              ) : animateSubmit ? (
                <><CheckCircle size={18} />送信完了！</>
              ) : savableMonthsCount > 1 ? (
                <><Send size={18} />{savableMonthsCount} ヶ月分をまとめて送信</>
              ) : (
                <><Send size={18} />この内容で送信</>
              )}
            </button>
          )}
          
          {!staffInfo && !isPastDeadline && (
            <div style={{ marginTop: "1rem", color: "#F44336", fontWeight: "500", fontSize: "0.85rem", display: "flex", alignItems: "center", justifyContent: "center", gap: "4px" }}>
              <AlertCircle size={16} />スタッフ情報を読み込み中...
            </div>
          )}
          
          {submitStatus && (
            <div style={{ 
              marginTop: "1rem",
              color: submitStatus.includes("失敗") ? "#F44336" : "#4CAF50",
              fontWeight: "500", animation: "fadeIn 0.5s ease",
              display: "flex", alignItems: "center", gap: "6px", justifyContent: "center"
            }}>
              {submitStatus.includes("失敗") ? <AlertCircle size={16} /> : <CheckCircle size={16} />}
              {submitStatus}
            </div>
          )}
          
          <div style={{
            marginTop: "1.5rem", padding: "0.75rem", backgroundColor: "#FFF8E1",
            borderRadius: "8px", fontSize: "0.85rem", color: "#795548",
            boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "8px", fontWeight: "600" }}>
              <Info size={16} />提出ルール
            </div>
            <ul style={{ margin: "0", paddingLeft: "1.2rem" }}>
              {sortedRequestGroups
                .filter(g => {
                  if (g.maxMonthlyRequestsByGroup && g.maxMonthlyRequestsByGroup.length > 0) {
                    const userLimits = g.maxMonthlyRequestsByGroup
                      .filter(item => userSkillGroups.some(ug => ug.id === item.skillGroupId))
                      .map(item => item.maxRequests);
                    if (userLimits.length > 0) return true;
                    return g.maxMonthlyRequests;
                  }
                  if (g.requestRuleComment) return true;
                  return g.maxMonthlyRequests;
                })
                .map(g => {
                  let effectiveLimit = g.maxMonthlyRequests;
                  if (g.maxMonthlyRequestsByGroup && g.maxMonthlyRequestsByGroup.length > 0) {
                    const userLimits = g.maxMonthlyRequestsByGroup
                      .filter(item => userSkillGroups.some(ug => ug.id === item.skillGroupId))
                      .map(item => item.maxRequests);
                    if (userLimits.length > 0) effectiveLimit = Math.max(...userLimits);
                  }
                  const ruleComment = getEffectiveComment(g);
                  return (
                    <li key={g.id} style={{ marginBottom: ruleComment ? "0.5rem" : "0" }}>
                      {effectiveLimit ? (
                        ruleComment ? <>{g.label}：{ruleComment}</> : <>{g.label}は最大月{effectiveLimit}回まで</>
                      ) : (
                        ruleComment && <>{g.label}：{ruleComment}</>
                      )}
                    </li>
                  );
                })
              }
              {sortedRequestGroups.filter(g => {
                if (g.maxMonthlyRequestsByGroup && g.maxMonthlyRequestsByGroup.length > 0) {
                  const userLimits = g.maxMonthlyRequestsByGroup
                    .filter(item => userSkillGroups.some(ug => ug.id === item.skillGroupId))
                    .map(item => item.maxRequests);
                  if (userLimits.length > 0) return true;
                  return g.maxMonthlyRequests;
                }
                if (g.requestRuleComment) return true;
                return g.maxMonthlyRequests;
              }).length === 0 && <li>希望回数の制限はありません</li>}
              <li>締め切り：{deadlineDateText}（23:59まで）</li>
              <li>提出後も締切日までは何度でも変更可能です</li>
            </ul>
          </div>
        </div>
      </div>
      
      {selectedDate && !isPastDeadline && !isViewOnly && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 1000, padding: "1rem", animation: "fadeIn 0.3s ease"
        }}>
          <div style={{
            background: "white", borderRadius: "20px", padding: "1.5rem",
            width: "100%", maxWidth: "400px",
            boxShadow: "0 12px 40px rgba(0,0,0,0.2)", animation: "slideUp 0.3s ease",
            maxHeight: "90vh", overflowY: "auto", marginBottom: "70px",
          }}>
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              marginBottom: "1rem", paddingBottom: "1rem", borderBottom: "2px solid #f0f0f0"
            }}>
              <h3 style={{ margin: 0, fontSize: "1.25rem", fontWeight: "700", color: "#333" }}>
                {formatDateShort(selectedDate)}
              </h3>
              <button
                onClick={() => setSelectedDate(null)}
                style={{ background: "transparent", border: "none", cursor: "pointer", padding: "4px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#f5f5f5"; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
              >
                <X size={24} color="#666" />
              </button>
            </div>

            <div style={{ marginBottom: "1rem" }}>
              <label style={{ display: "block", fontWeight: "600", marginBottom: "0.75rem", color: "#333", fontSize: "0.95rem" }}>
                希望内容
              </label>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                {requestOptions.map((option) => (
                  <label
                    key={option.value}
                    style={{
                      display: "flex", alignItems: "center", padding: "0.75rem", borderRadius: "12px",
                      border: selectedType === option.value ? `2px solid ${option.color}` : "1px solid #ddd",
                      backgroundColor: selectedType === option.value ? `${option.color}15` : "white",
                      cursor: "pointer", transition: "all 0.2s ease",
                      fontWeight: selectedType === option.value ? "600" : "400"
                    }}
                    onMouseEnter={(e) => { if (selectedType !== option.value) e.currentTarget.style.backgroundColor = "#f9f9f9"; }}
                    onMouseLeave={(e) => { if (selectedType !== option.value) e.currentTarget.style.backgroundColor = "white"; }}
                  >
                    <input
                      type="radio" name="shift-type" value={option.value}
                      checked={selectedType === option.value}
                      onChange={() => setSelectedType(option.value)}
                      style={{ marginRight: "0.75rem", cursor: "pointer" }}
                    />
                    <span style={{ color: option.color }}>{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: "1.5rem" }}>
              <label style={{ display: "block", fontWeight: "600", marginBottom: "0.5rem", color: "#333", fontSize: "0.95rem" }}>
                備考（任意）
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="特記事項があれば入力してください"
                style={{
                  width: "100%", minHeight: "80px", padding: "0.75rem", borderRadius: "8px",
                  border: "1px solid #ddd", fontSize: "0.9rem", fontFamily: "inherit",
                  resize: "vertical", transition: "border 0.2s ease", boxSizing: "border-box"
                }}
                onFocus={(e) => { e.currentTarget.style.borderColor = "#667eea"; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = "#ddd"; }}
              />
            </div>

            <button
              onClick={handleModalSubmit}
              style={{
                width: "100%", padding: "0.875rem",
                background: "linear-gradient(135deg, #4CAF50 0%, #45a049 100%)",
                color: "white", border: "none", borderRadius: "12px",
                fontWeight: "600", cursor: "pointer", fontSize: "1rem",
                boxShadow: "0 2px 8px rgba(76, 175, 80, 0.3)", transition: "all 0.2s ease",
                display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 4px 12px rgba(76, 175, 80, 0.4)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 2px 8px rgba(76, 175, 80, 0.3)"; }}
            >
              <Check size={18} />保存
            </button>
          </div>
        </div>
      )}
      
      {/* 変更希望モーダル (ロック月) */}
      {changeRequestOpen && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 1000, padding: "1rem", animation: "fadeIn 0.3s ease"
        }}>
          <div style={{
            background: "white", borderRadius: "20px", padding: "1.5rem",
            width: "100%", maxWidth: "440px",
            boxShadow: "0 12px 40px rgba(0,0,0,0.2)", animation: "slideUp 0.3s ease",
            maxHeight: "90vh", overflowY: "auto", marginBottom: "70px",
          }}>
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              marginBottom: "1rem", paddingBottom: "1rem", borderBottom: "2px solid #f0f0f0"
            }}>
              <h3 style={{ margin: 0, fontSize: "1.15rem", fontWeight: 700, color: "#333" }}>
                <MessageSquare size={18} style={{ verticalAlign: 'middle', marginRight: 6, color: "#d97706" }} />
                変更希望を送る
              </h3>
              <button
                onClick={() => setChangeRequestOpen(false)}
                style={{ background: "transparent", border: "none", cursor: "pointer", padding: "4px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}
              >
                <X size={22} color="#666" />
              </button>
            </div>

            <div style={{
              background: "#fff7ed", border: "1px solid #fed7aa",
              borderRadius: 8, padding: "0.75rem", marginBottom: "1rem",
              fontSize: "0.85rem", color: "#9a3412",
            }}>
              <strong>{monthLabel(targetMonth)}</strong>
              {changeRequestDate ? ` / ${formatDateShort(changeRequestDate)}` : ' の全体'}
              <br />
              この月の希望受付は締め切り済みです。管理者に変更希望として通知します。
            </div>

            <div style={{ marginBottom: "1rem" }}>
              <label style={{ display: "block", fontWeight: 600, marginBottom: "0.5rem", color: "#333", fontSize: "0.9rem" }}>
                希望する変更内容 (任意)
              </label>
              <select
                value={changeRequestDesired}
                onChange={(e) => setChangeRequestDesired(e.target.value)}
                style={{
                  width: "100%", padding: "0.6rem", borderRadius: 8,
                  border: "1px solid #ddd", fontSize: "0.9rem", boxSizing: "border-box"
                }}
              >
                <option value="">選択なし (備考欄で説明)</option>
                {sortedRequestGroups.map((g) => (
                  <option key={g.id} value={g.id}>{g.label}</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: "1.5rem" }}>
              <label style={{ display: "block", fontWeight: 600, marginBottom: "0.5rem", color: "#333", fontSize: "0.9rem" }}>
                変更したい理由 <span style={{ color: "#dc2626" }}>*</span>
              </label>
              <textarea
                value={changeRequestNote}
                onChange={(e) => setChangeRequestNote(e.target.value)}
                placeholder="例: 急な用事ができたため、◯日の希望を変更したいです。"
                style={{
                  width: "100%", minHeight: 100, padding: "0.75rem", borderRadius: 8,
                  border: "1px solid #ddd", fontSize: "0.9rem", fontFamily: "inherit",
                  resize: "vertical", boxSizing: "border-box"
                }}
              />
            </div>

            {changeRequestStatus === 'ok' && (
              <div style={{ marginBottom: "0.75rem", color: "#16a34a", fontSize: "0.85rem", display: "flex", alignItems: "center", gap: 6 }}>
                <CheckCircle size={16} />送信しました
              </div>
            )}
            {changeRequestStatus === 'error' && (
              <div style={{ marginBottom: "0.75rem", color: "#dc2626", fontSize: "0.85rem", display: "flex", alignItems: "center", gap: 6 }}>
                <AlertCircle size={16} />送信に失敗しました
              </div>
            )}

            <button
              onClick={submitChangeRequest}
              disabled={changeRequestSubmitting || !changeRequestNote.trim()}
              style={{
                width: "100%", padding: "0.875rem",
                background: (changeRequestSubmitting || !changeRequestNote.trim())
                  ? "#9e9e9e"
                  : "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                color: "white", border: "none", borderRadius: 12,
                fontWeight: 600, fontSize: "1rem",
                cursor: (changeRequestSubmitting || !changeRequestNote.trim()) ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              }}
            >
              {changeRequestSubmitting ? (
                <><Loader2 size={18} style={{ animation: "spin 1s linear infinite" }} />送信中...</>
              ) : (
                <><Send size={18} />管理者に送信</>
              )}
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

export default MobileShiftForm;